import type { FC } from 'react';
import { useCallback } from 'react';
import { Button, Divider, ListItem, Menu, MenuItem, styled, Typography } from '@mui/material';
import { HelpOutline, TaskOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { useAnchorMenu } from '../../hooks';
import Box from '@mui/material/Box';
import { DOC_WEB_SERVICE, DOC_WIMAS } from '../../constants';

const SupportButtonStyled = styled(Button)(({ theme }) => ({
  width: 32,
  minWidth: 32,
  height: 32,
  minHeight: 32,
  p: 0,
  textAlign: 'center',
  borderRadius: '16px',
  fontSize: theme.typography.body2.fontSize,
  color: theme.palette.text.primary,
}));

const SupportButton: FC = () => {
  const { isOpen, anchor, handleOpenMenu, handleCloseMenu } = useAnchorMenu();

  const handleDocumentationClick = useCallback(() => {
    window && window.open(DOC_WEB_SERVICE, '_blank', 'noopener,noreferrer');
    handleCloseMenu();
  }, [handleCloseMenu]);

  const handleReportClick = useCallback(() => {
    window && window.open(DOC_WIMAS, '_blank', 'noopener,noreferrer');
    handleCloseMenu();
  }, [handleCloseMenu]);

  return (
    <>
      <SupportButtonStyled
        variant={'text'}
        id="support-nav-button"
        aria-controls={isOpen ? 'support-nav' : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? 'true' : undefined}
        disableElevation
        disableRipple
        disableTouchRipple
        onClick={handleOpenMenu}
      >
        <HelpOutline sx={{ fontSize: '28px' }} color="primary" />
      </SupportButtonStyled>
      <Menu
        id="support-nav"
        slotProps={{
          paper: {
            sx: {
              width: 250,
              ml: 1,
            },
          },
        }}
        sx={{ '.MuiList-padding.MuiMenu-list': { pt: 0 } }}
        elevation={8}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        anchorEl={anchor}
        open={isOpen}
        onClose={handleCloseMenu}
      >
        <ListItem divider sx={{ px: 1.5, pt: 2.5, pb: 2, gap: 1.5, backgroundColor: 'rgba(41, 121, 255, 0.08)' }}>
          <HelpOutline fontSize="medium" color="primary" />
          <Typography
            sx={(t) => ({
              fontSize: t.typography.subtitle1.fontSize,
              fontWeight: 400,
              color: t.palette.primary.dark,
            })}
          >
            Wellpilot support
          </Typography>
        </ListItem>
        <MenuItem
          onClick={handleDocumentationClick}
          disableTouchRipple
          disableRipple
          sx={t => ({ color: t.palette.action.active, py: 1.5, gap: 1.5 })}
        >
          <TaskOutlined sx={{ color: 'action.active' }} fontSize="medium" />
          <Box>
            <Typography
              sx={(t) => ({
                fontSize: t.typography.body2.fontSize,
                color: t.palette.text.primary,
                fontWeight: 400,
              })}
            >
              Documentation
            </Typography>
            <Typography
              sx={(t) => ({
                fontSize: t.typography.caption.fontSize,
                color: t.palette.primary.main,
              })}
            >
              Before reporting in Slack
            </Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ '&.MuiDivider-root.MuiDivider-fullWidth': { mt: 0, mb: 0 } }} />
        <MenuItem
          onClick={handleReportClick}
          disableTouchRipple
          disableRipple
          sx={t => ({ color: t.palette.action.active, py: 1.5, gap: 1.5 })}
        >
          <WarningAmberOutlined sx={{ color: 'action.active' }} fontSize="medium" />
          <Box>
            <Typography
              sx={(t) => ({
                fontSize: t.typography.body2.fontSize,
                color: t.palette.text.primary,
                fontWeight: 400,
              })}
            >
              Report in WIMAS
            </Typography>
            <Typography
              sx={(t) => ({
                fontSize: t.typography.caption.fontSize,
                color: t.palette.error.main,
              })}
            >
              For critical issues
            </Typography>
          </Box>
        </MenuItem>
      </Menu>
    </>
  );
};

export default SupportButton;